import React from 'react';
import { motion } from 'framer-motion';
import { useScroll, useTransform } from 'framer-motion';
import { FaArrowRight, FaPlay, FaCheckCircle, FaAward, FaTrophy, FaChevronDown } from 'react-icons/fa';
import PremiumButton from './ui/PremiumButton';
import logoImg from '../assets/logo.svg';

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center pt-24 pb-20 px-4 overflow-hidden">
      {/* Background orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[
          'bg-cyan-500/10 top-20 left-10',
          'bg-orange-500/10 top-40 right-20',
          'bg-blue-500/10 bottom-20 left-1/3',
        ].map((cls, i) => (
          <motion.div key={i} className={`absolute w-96 h-96 rounded-full blur-3xl ${cls}`}
            style={{ willChange: 'transform' }}
            animate={{ y: [0, -30, 0], x: [0, i % 2 === 0 ? 20 : -20, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 8 + i * 2, repeat: Infinity, delay: i * 2 }} />
        ))}
      </div>

      <motion.div style={{ y, opacity }} className="max-w-7xl mx-auto text-center relative z-10">
        {/* Badge */}
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }} className="inline-block mb-8">
          <div className="px-6 py-3 bg-gradient-to-r from-cyan-500/10 to-orange-500/10 border border-cyan-500/20 rounded-full backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium bg-gradient-to-r from-cyan-400 to-orange-400 bg-clip-text text-transparent">
                Trusted by 500+ Businesses
              </span>
            </div>
          </div>
        </motion.div>

        <motion.h1 className="text-6xl md:text-8xl lg:text-9xl font-black mb-8 leading-[1.1] tracking-tight"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <span className="block">Your Growth.</span>
          <span className="block bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-600 bg-clip-text text-transparent">Our Strategy.</span>
          <span className="block text-5xl md:text-7xl lg:text-8xl mt-4 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent">
            Unlimited Possibilities.
          </span>
        </motion.h1>

        <motion.p className="text-xl md:text-2xl lg:text-3xl text-gray-600 dark:text-gray-400 mb-6 font-light tracking-wide max-w-4xl mx-auto"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
          Your All-In-One Digital Partner — Social Media · Branding · Web · Ads · Video
        </motion.p>

        <motion.div className="flex items-center justify-center gap-6 mb-16 flex-wrap"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
          {[
            { icon: FaCheckCircle, text: 'ROI Guaranteed' },
            { icon: FaAward, text: 'Award Winning' },
            { icon: FaTrophy, text: 'Industry Leader' },
          ].map((item, i) => (
            <motion.div key={i} whileHover={{ scale: 1.05 }} className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <item.icon className="text-cyan-400 text-lg" />
              <span className="text-sm font-medium">{item.text}</span>
            </motion.div>
          ))}
        </motion.div>

        <motion.div className="flex flex-col sm:flex-row gap-6 justify-center items-center"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
          <PremiumButton primary icon={<FaArrowRight />}>Start Your Journey</PremiumButton>
          <PremiumButton icon={<FaPlay />}>Watch Demo</PremiumButton>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.8, y: 50 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 1, duration: 1.2, type: 'spring' }} className="mt-24 relative inline-block">
          <div className="absolute inset-0 bg-cyan-500 blur-[80px] opacity-30 animate-pulse rounded-full pointer-events-none" style={{ willChange: 'opacity' }} />
          <motion.img src={logoImg} alt="Funnel Logo" className="w-72 h-72 mx-auto object-contain relative z-10"
            style={{ filter: 'drop-shadow(0 0 25px rgba(34,211,238,0.9))', willChange: 'transform' }}
            animate={{ y: [0, -20, 0], rotate: [0, 3, -3, 0], scale: [1, 1.02, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} />
        </motion.div>
      </motion.div>

      <motion.div animate={{ y: [0, 15, 0] }} transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2">
        <span className="text-xs text-gray-500 uppercase tracking-widest">Scroll</span>
        <FaChevronDown className="text-cyan-400 text-2xl" />
      </motion.div>
    </section>
  );
}
